import {EMPTY, Observable, Subject, concat, expand, filter, forkJoin, isObservable, last, map, of, shareReplay, tap} from 'rxjs';
import {IterableUtil, MapUtils} from '../util/collections/collection-utils';

export type UpdateFunction<Id, T> = (id: Id, item: T) => Observable<T>;

export class CacheById<Id, T> {
  private readonly cache: Map<Id, T | Observable<Map<Id, T>>> = new Map();
  private readonly updatedItemsByIdSubject = new Subject<Map<Id, T>>();
  private readonly delayedUpdates = new Map<Id, UpdateFunction<Id, T>[]>();

  constructor(
    private fetchFn: (ids: Id[]) => Observable<Map<Id, T> | null>
  ) {}

  getById(id: Id): Observable<T> {
    return this.getByIds([id]).pipe(map(itemById => itemById.get(id)));
  }

  getByIds(ids: Id[]): Observable<Map<Id, T>> {
    const uncachedIds = new Set<Id>();
    const cachedItemsById = new Map<Id, T>();
    const fetches = new Set<Observable<Map<Id, T>>>();

    ids.forEach(id => {
      const cachedItem = this.cache.get(id);
      if (isObservable(cachedItem)) {
        fetches.add(cachedItem);
      } else if (cachedItem !== undefined) {
        cachedItemsById.set(id, cachedItem);
      } else {
        uncachedIds.add(id);
      }
    });

    if (uncachedIds.size) {
      fetches.add(this.fetchAndCache(Array.from(uncachedIds)));
    }

    return fetches.size
      ? forkJoin(Array.from(fetches)).pipe(
          map(itemsByIdList => {
            itemsByIdList.forEach(itemsById =>
              itemsById?.forEach((item, id) =>
                cachedItemsById.set(id, item)
              )
            );
            // keeps order the same as ids parameter
            return new Map(ids.map(id => ([id, cachedItemsById.get(id)] as [Id, T])));
          })
        )
      : of(cachedItemsById);
  }

  updateItemsIfExists(ids: Id[], mapFn: UpdateFunction<Id, T>): Observable<Map<Id, T>> {
    return this.updateItems(ids.filter(id => this.cache.has(id)), mapFn);
  }

  setItems(itemObservableById: Map<Id, Observable<T>>): Observable<Map<Id, T>> {
    return this.updateItems(
      Array.from(itemObservableById.keys()),
      id => itemObservableById.get(id)
    );
  }

  deleteById(alertNotificationId: Id) {
    this.cache.delete(alertNotificationId);
    this.delayedUpdates.delete(alertNotificationId);
  }

  watch(ids: Id[]): Observable<Map<Id, T>> {
    return concat(this.getByIds(ids), this.onIdsUpdate(ids));
  }

  clear() {
    this.cache.clear();
    this.delayedUpdates.clear();
  }

  private updateItems(ids: Id[], mapFn: UpdateFunction<Id, T>): Observable<Map<Id, T>> {
    ids.forEach(id => {
      const item = this.cache.get(id);
      if (isObservable(item)) {
        MapUtils.setDefault(this.delayedUpdates, id, []).push(mapFn);
      } else {
        const updatedItem$ = mapFn(id, item).pipe(
          map(updatedItem => new Map([[id, updatedItem]])),
          expand((updatedItemsById) => this.applyDelayedUpdates(updatedItemsById)),
          last(),
          tap(itemsById => this.updateCacheWithValue(itemsById)),
          shareReplay(1)
        );
        this.cache.set(id, updatedItem$);
      }
    });
    const updatedItems$ = this.getByIds(ids).pipe(shareReplay(1));
    updatedItems$.subscribe();
    return updatedItems$;
  }

  private onIdsUpdate(ids: Id[]): Observable<Map<Id, T>> {
    const watchedIdSet = new Set(ids);
    return this.updatedItemsByIdSubject.pipe(
      map(updatedItemsById => IterableUtil.reduce(
        updatedItemsById,
        (acc, [id, item]) => watchedIdSet.has(id)
          ? acc.set(id, item)
          : acc,
        new Map<Id, T>()
      )),
      filter(watchedUpdatedItemsById => !!watchedUpdatedItemsById.size)
    );
  }

  private fetchAndCache(ids: Id[]): Observable<Map<Id, T>> {
    const fetch$ = this.fetchFn(ids).pipe(
      expand(fetchedItemsById => this.applyDelayedUpdates(fetchedItemsById)),
      last(),
      tap(itemsById => {
        ids.forEach(id => MapUtils.setDefault(itemsById, id, null));
        this.updateCacheWithValue(itemsById);
      }),
      shareReplay(1)
    );
    ids.forEach(id => this.cache.set(id, fetch$.pipe(map(itemById => new Map([[id, itemById?.get(id) ?? null]])))));
    return fetch$;
  }

  private updateCacheWithValue(itemsById: Map<Id, T>): void {
    itemsById.forEach((item, id) => this.cache.set(id, item ?? null));
    this.updatedItemsByIdSubject.next(itemsById);
  }

  private applyDelayedUpdates(itemsById: Map<Id, T>): Observable<Map<Id, T>> {
    let haveUpdatesToApply = false;
    const delayedUpdateList: Observable<[Id, T]>[] = IterableUtil.reduce(
      itemsById,
      (updatedTupleList, [id, item]) => {
        const updateFns = this.delayedUpdates.get(id) ?? null;
        if (updateFns?.length) {
          const updatedItemTuple$ = of(item).pipe(
            expand(updatingItem => updateFns.shift()?.(id, updatingItem) ?? EMPTY),
            last(),
            map(updatingItem => <[Id, T]>[id, updatingItem])
          );
          updatedTupleList.push(updatedItemTuple$);
          haveUpdatesToApply = true;
        } else {
          updatedTupleList.push(of([id, item]));
        }
        return updatedTupleList;
      },
      <Observable<[Id, T]>[]>[]
    );
    if (haveUpdatesToApply) {
      return forkJoin(delayedUpdateList).pipe(map(tuples$ => tuples$.reduce((acc, [id, item]) => acc.set(id, item), new Map<Id, T>())));
    } else {
      return EMPTY;
    }
  }
}
